import {
  BaseNodeFields,
  BranchNodeFormValues,
  getBranchFieldPath,
  getNetworkFieldPath,
  getSubBranchFieldPath,
  NetworkNodeFieldPath,
  NetworkNodeFormValues,
  SubBranchNodeFormValues,
} from '../types';

export type NetworkNodeEntry = {
  path: NetworkNodeFieldPath;
  node: BaseNodeFields & {id: string};
};

export const getNodeAtPath = (
  network: NetworkNodeFormValues[],
  path: NetworkNodeFieldPath,
): (BaseNodeFields & {id: string}) | null => {
  const parts = path.split('.');
  if (parts.length === 2) {
    return network[Number.parseInt(parts[1], 10)] ?? null;
  }
  if (parts.length === 4) {
    const i = Number.parseInt(parts[1], 10);
    const j = Number.parseInt(parts[3], 10);
    return network[i]?.branches[j] ?? null;
  }
  if (parts.length === 6) {
    const i = Number.parseInt(parts[1], 10);
    const j = Number.parseInt(parts[3], 10);
    const k = Number.parseInt(parts[5], 10);
    return network[i]?.branches[j]?.subBranches[k] ?? null;
  }
  return null;
};

export const walkNetworkNodes = (
  network: NetworkNodeFormValues[],
): NetworkNodeEntry[] => {
  const entries: NetworkNodeEntry[] = [];

  network.forEach((networkNode, i) => {
    entries.push({path: getNetworkFieldPath(i), node: networkNode});

    networkNode.branches.forEach((branch, j) => {
      entries.push({path: getBranchFieldPath(i, j), node: branch});

      (branch.subBranches ?? []).forEach((subBranch, k) => {
        entries.push({
          path: getSubBranchFieldPath(i, j, k),
          node: subBranch,
        });
      });
    });
  });

  return entries;
};

export const formatUsageScreensMessage = (screens: string[]): string =>
  screens.join(', ');

export type BlockActionResult =
  | {allowed: true}
  | {allowed: false; message: string};

const NODE_TAB_DATA_FIELDS: {name: keyof BaseNodeFields; message: string}[] = [
  {
    name: 'departments',
    message: 'לצומת יש מחלקות משויכות ולא ניתן למחוק אותו.',
  },
  {
    name: 'roles',
    message: 'לצומת יש תפקידים משויכים ולא ניתן למחוק אותו.',
  },
  {
    name: 'shifts',
    message: 'לצומת יש משמרות משויכות ולא ניתן למחוק אותו.',
  },
  {
    name: 'zones',
    message: 'לצומת יש אזורים משויכים ולא ניתן למחוק אותו.',
  },
  {
    name: 'users',
    message: 'לצומת יש משתמשים משויכים ולא ניתן למחוק אותו.',
  },
  {
    name: 'checklists',
    message: 'לצומת יש רשימות בדיקה משויכות ולא ניתן למחוק אותו.',
  },
  {
    name: 'regulations',
    message: 'לצומת יש תקנות משויכות ולא ניתן למחוק אותו.',
  },
];

const getNodeTabDataBlock = (node: BaseNodeFields): BlockActionResult | null => {
  for (const field of NODE_TAB_DATA_FIELDS) {
    const value = node[field.name];
    if (Array.isArray(value) && value.length > 0) {
      return {allowed: false, message: field.message};
    }
  }

  return null;
};

export const canDeleteNode = (
  node: NetworkNodeFormValues | BranchNodeFormValues | SubBranchNodeFormValues,
): BlockActionResult => {
  const selfBlock = getNodeTabDataBlock(node);
  if (selfBlock) return selfBlock;

  if ('subBranches' in node) {
    for (const subBranch of node.subBranches ?? []) {
      const block = canDeleteNode(subBranch);
      if (!block.allowed) return block;
    }
  }

  if ('branches' in node) {
    for (const branch of node.branches ?? []) {
      const block = canDeleteNode(branch);
      if (!block.allowed) return block;
    }
  }

  return {allowed: true};
};
